export type Difficulty = 'slow' | 'normal' | 'fast' | 'custom';
export type GameMode = 'practice' | 'attack' | 'no_miss' | 'hard_hidden';

export const gameModeDetails: Record<GameMode, { label: string; description: string }> = {
  practice: { label: 'Luyện thường', description: '3 mạng · power-up bật · chơi đến khi hết mạng' },
  attack: { label: '60 giây', description: '60 giây · tính điểm & combo · không power-up' },
  no_miss: { label: 'Không mất mạng', description: '60 giây hoàn hảo · sai một câu là kết thúc' },
  hard_hidden: { label: 'Ẩn tiếng Anh', description: '3 mạng · chỉ hiện nghĩa Việt · power-up bật' },
};

export const isChallengeMode = (mode: GameMode) => mode === 'attack' || mode === 'no_miss';
export const usesLives = (mode: GameMode) => mode === 'practice' || mode === 'hard_hidden';
export const allowsPause = (mode: GameMode) => !isChallengeMode(mode);
export const allowsPowerUps = (mode: GameMode) => !isChallengeMode(mode);
export const endsImmediatelyOnMiss = (mode: GameMode) => mode === 'no_miss';

export type WordEntry = {
  id: string;
  prompt: string;
  translation: string;
  entryType: 'word' | 'phrase';
  position: number;
};

export type MixedWordEntry = WordEntry & { sourceSetId: string; sourceSetTitle: string };

export type WordSet = {
  id: string;
  title: string;
  description: string;
  topic: string;
  isSystem: boolean;
  entryCount: number;
  entries?: WordEntry[];
};

export function mixEntries(wordSets: Array<Pick<WordSet, 'id' | 'title' | 'entries'>>): MixedWordEntry[] {
  return wordSets.flatMap((wordSet) => (wordSet.entries ?? []).map((entry) => ({ ...entry, sourceSetId: wordSet.id, sourceSetTitle: wordSet.title })));
}

export type User = { id: string; email: string; displayName: string; avatarUrl: string | null };

const baseSeconds: Record<Difficulty, number> = { slow: 20, normal: 13, fast: 8, custom: 13 };
const extendedTravelMultiplier = 1.35;
export const customFallSecondsMin = 5;
export const customFallSecondsMax = 35;

export const difficultyLabels: Record<Difficulty, string> = { slow: '27 giây', normal: '17.5 giây', fast: '11 giây', custom: '-- giây' };

export function fallDuration(prompt: string, difficulty: Difficulty, level = 1, customFallSeconds?: number) {
  const base = difficulty === 'custom' && typeof customFallSeconds === 'number' ? customFallSeconds : baseSeconds[difficulty];
  const lengthBonus = Math.min(7, Math.max(0, prompt.trim().length - 12) * 0.055);
  const levelMultiplier = difficulty === 'slow' ? 1 : Math.min(1.35, 1 + Math.max(0, level - 1) * 0.05);
  return ((base + lengthBonus) / levelMultiplier) * extendedTravelMultiplier;
}

export function normalizeTyping(value: string) {
  return value.trim().replace(/[.?!]+$/g, '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('en-US');
}

export function displayGameText(value: string) {
  return normalizeTyping(value);
}

export type CompletionFlash = { prompt: string; lane: number; progress: number };
export function createCompletionFlash(prompt: string, lane: number, startedAt: number, durationSeconds: number, currentTime: number): CompletionFlash {
  return { prompt: displayGameText(prompt), lane, progress: Math.min(1, Math.max(0, (currentTime - startedAt) / (durationSeconds * 1000))) };
}

export function shuffleEntries(entries: WordEntry[], lastEntryId: string | null = null, random: () => number = Math.random) {
  const shuffled = [...entries];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]];
  }
  if (shuffled.length > 1 && shuffled[0].id === lastEntryId) [shuffled[0], shuffled[1]] = [shuffled[1], shuffled[0]];
  return shuffled;
}

export function selectFreeLane(laneCount: number, occupiedLanes: Iterable<number>, random: () => number = Math.random) {
  const occupied = new Set(occupiedLanes);
  const free = Array.from({ length: laneCount }, (_, lane) => lane).filter((lane) => !occupied.has(lane));
  return free.length ? free[Math.floor(random() * free.length)] : null;
}

export function parseCsv(text: string): Array<{ prompt: string; translation: string }> {
  const rows = text.trim().split(/\r?\n/).filter(Boolean);
  if (!rows.length) return [];
  const parseLine = (line: string) => {
    const cells: string[] = [];
    let cell = '', quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"' && line[i + 1] === '"') { cell += '"'; i += 1; }
      else if (char === '"') quoted = !quoted;
      else if (char === ',' && !quoted) { cells.push(cell.trim()); cell = ''; }
      else cell += char;
    }
    cells.push(cell.trim());
    return cells;
  };
  const header = parseLine(rows[0]).map((value) => value.toLowerCase());
  if (header[0] !== 'prompt' || header[1] !== 'translation') throw new Error('CSV cần header: prompt,translation');
  return rows.slice(1).map((row) => {
    const [prompt = '', translation = ''] = parseLine(row);
    return { prompt, translation };
  });
}
